import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { useBackendApi } from "@/hooks/fetch";
import { useOpenPlayerModal } from "@/hooks/playerModal";
import { dateToLocaleDateString } from "@/lib/dateTime";
import { cn } from "@/lib/utils";
import type { WeeklyPlaytimePlayer, WeeklyPlaytimeResp } from "@shared/playerApiTypes";
import { HourglassIcon, Loader2Icon, RefreshCcwIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

type LoadedResp = Exclude<WeeklyPlaytimeResp, { error: string }>;

/** Minutes as "12 Std. 30 Min." - the ranking is read at a glance, so no seconds. */
const formatMinutes = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    if (!hours) return `${rest} Min.`;
    if (!rest) return `${hours} Std.`;
    return `${hours} Std. ${rest} Min.`;
};

/** The top three get a tint, everyone else stays quiet. */
const rankTone = (rank: number) => {
    if (rank === 1) return 'text-amber';
    if (rank === 2) return 'text-dim';
    if (rank === 3) return 'text-jail-inline';
    return 'text-faint';
};


function RankRow({ player, rank, maxMinutes, onOpen }: {
    player: WeeklyPlaytimePlayer;
    rank: number;
    maxMinutes: number;
    onOpen: () => void;
}) {
    //bar is relative to the leader, so the spread stays readable on any server
    const share = maxMinutes > 0 ? (player.weekPlayTime / maxMinutes) * 100 : 0;

    return (
        <div
            className="relative flex items-center gap-3 p-2.5 cursor-pointer hover:bg-tile transition-colors"
            onClick={onOpen}
            title={`Gesamt: ${formatMinutes(player.playTime)}`}
        >
            <div
                className="absolute inset-y-0 left-0 bg-accent/10 pointer-events-none"
                style={{ width: `${share}%` }}
                aria-hidden
            />
            <span className={cn('relative w-8 shrink-0 text-right tabular font-highlight', rankTone(rank))}>
                {rank}
            </span>
            <span className="relative truncate-flex grow font-semibold">
                {player.displayName}
            </span>
            {player.isOnline ? (
                <span className="relative shrink-0 text-micro uppercase tracking-[0.1em] text-accent">
                    online
                </span>
            ) : null}
            <span className="relative shrink-0 tabular text-caption">
                {formatMinutes(player.weekPlayTime)}
            </span>
        </div>
    );
}


export default function WeeklyPlaytimePage() {
    const openPlayerModal = useOpenPlayerModal();
    const [data, setData] = useState<LoadedResp | undefined>();
    const [loadError, setLoadError] = useState<string | undefined>();
    const [isRefreshing, setIsRefreshing] = useState(false);

    const api = useBackendApi<WeeklyPlaytimeResp>({
        method: 'GET',
        path: '/playtime/weekly',
        abortOnUnmount: true,
    });

    const refresh = useCallback(async () => {
        setIsRefreshing(true);
        const resp = await api({
            error: (msg) => setLoadError(msg),
            finally: () => setIsRefreshing(false),
        });
        if (!resp) return;
        if ('error' in resp) {
            setLoadError(resp.error);
            return;
        }
        setLoadError(undefined);
        setData(resp);
    }, []);

    useEffect(() => { refresh(); }, []);

    const weekLabel = data
        ? `${dateToLocaleDateString(new Date(data.weekStartTs), 'medium')} – ${dateToLocaleDateString(new Date(data.weekEndTs - 1), 'medium')}`
        : null;
    const maxMinutes = data?.players[0]?.weekPlayTime ?? 0;

    return (
        <div className="space-y-4 w-full">
            <PageHeader icon={<HourglassIcon />} title="Spielzeit der Woche" />

            {loadError ? (
                <div className="surface p-4 text-danger-soft">{loadError}</div>
            ) : !data ? (
                <div className="flex justify-center p-8 text-faint">
                    <Loader2Icon className="animate-spin size-6" />
                </div>
            ) : (<>
                <div className="surface p-4 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <p className="text-label uppercase tracking-[0.1em] text-faint">Zeitraum</p>
                        <p className="text-value font-highlight">{weekLabel}</p>
                        <p className="text-caption text-faint">
                            Montag 00:00 bis Montag 00:00 · setzt sich automatisch zurück
                        </p>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="text-right">
                            <p className="text-label uppercase tracking-[0.1em] text-faint">Spieler</p>
                            <p className="text-value tabular font-highlight">{data.totalPlayers}</p>
                        </div>
                        <div className="text-right">
                            <p className="text-label uppercase tracking-[0.1em] text-faint">Gesamt</p>
                            <p className="text-value tabular font-highlight">{formatMinutes(data.totalMinutes)}</p>
                        </div>
                        <Button
                            size="sm"
                            variant="muted"
                            onClick={refresh}
                            disabled={isRefreshing}
                            title="Neu laden"
                        >
                            <RefreshCcwIcon className={cn('size-4', isRefreshing && 'animate-spin')} />
                        </Button>
                    </div>
                </div>

                {data.players.length ? (<>
                    <div className="surface divide-y divide-edge overflow-hidden">
                        {data.players.map((player, index) => (
                            <RankRow
                                key={player.license}
                                player={player}
                                rank={index + 1}
                                maxMinutes={maxMinutes}
                                onOpen={() => openPlayerModal({ license: player.license })}
                            />
                        ))}
                    </div>
                    {data.totalPlayers > data.players.length ? (
                        <p className="text-caption text-faint text-center">
                            Zeigt die ersten {data.players.length} von {data.totalPlayers} Spielern.
                        </p>
                    ) : null}
                </>) : (
                    <div className="surface p-8 text-center text-muted-foreground">
                        <p>Diese Woche war noch niemand online.</p>
                        <p className="text-caption opacity-75">
                            Die Erfassung beginnt mit der ersten gespielten Minute.
                        </p>
                    </div>
                )}
            </>)}
        </div>
    );
}
